import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { webEngine, type DownloadTicket, type GameDetail, type GameSummary } from '@replay/shared';
import { api } from './api.ts';
import { getCache } from './cache.ts';

/**
 * REPLAY Offline.
 *
 * What makes a game playable without a connection on this device:
 *  1. the app shell and emulator loader (precached by the service worker),
 *  2. the emulator core for its system (cached when played or downloaded),
 *  3. the game file itself (GameCache, integrity-checked by sha256),
 *  4. its game sheet (stored here, in localStorage),
 *  5. the last known profile (lib/auth.tsx).
 * Saves made offline are kept locally and synced on reconnection (saveSync.ts).
 */

// ---------------------------------------------------------------------------
// Connectivity
// ---------------------------------------------------------------------------
let online = typeof navigator === 'undefined' ? true : navigator.onLine;
const listeners = new Set<(v: boolean) => void>();
function setOnline(v: boolean) {
  if (v === online) return;
  online = v;
  for (const l of listeners) l(v);
  if (v) window.dispatchEvent(new Event('replay:online'));
}
export function isOnline() {
  return online;
}

let probing = false;
/** Confirm reachability of REPLAY itself (navigator.onLine is only a hint). */
export async function probe() {
  if (probing) return online;
  probing = true;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    const r = await fetch('/api/health', { cache: 'no-store', signal: ctrl.signal });
    clearTimeout(t);
    setOnline(r.ok);
  } catch {
    setOnline(false);
  } finally {
    probing = false;
  }
  return online;
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void probe());
  window.addEventListener('offline', () => setOnline(false));
  // Network failures seen by the API layer flip us offline; successes back online.
  window.addEventListener('replay:network', (e) => setOnline((e as CustomEvent<boolean>).detail));
  // While offline, check now and then whether REPLAY is reachable again.
  setInterval(() => {
    if (!online && !document.hidden) void probe();
  }, 20_000);
}

export function useOnline() {
  const [v, setV] = useState(online);
  useEffect(() => {
    listeners.add(setV);
    setV(online);
    return () => void listeners.delete(setV);
  }, []);
  return v;
}

// ---------------------------------------------------------------------------
// Game sheets kept on this device
// ---------------------------------------------------------------------------
const DETAIL_KEY = (id: string) => `replay.game.${id}`;

export function rememberGame(g: GameDetail) {
  try {
    const json = JSON.stringify(g);
    localStorage.setItem(DETAIL_KEY(g.id), json);
    localStorage.setItem(DETAIL_KEY(g.slug), json);
  } catch {
    /* storage full or unavailable */
  }
}

export function recallGame(idOrSlug: string): GameDetail | null {
  try {
    const v = localStorage.getItem(DETAIL_KEY(idOrSlug));
    return v ? (JSON.parse(v) as GameDetail) : null;
  } catch {
    return null;
  }
}

/** Games whose file is fully downloaded on this device and whose sheet we know. */
export async function localGames(): Promise<(GameSummary & { localSize: number; pinned: boolean; lastUsedAt: number })[]> {
  const cache = await getCache();
  const entries = (await cache.list()).filter((e) => e.state === 'complete');
  const out: (GameSummary & { localSize: number; pinned: boolean; lastUsedAt: number })[] = [];
  const seen = new Set<string>();
  for (const e of entries) {
    for (const id of e.gameIds) {
      const g = recallGame(id);
      if (!g || seen.has(g.id) || g.file?.sha256 !== e.sha256) continue;
      seen.add(g.id);
      out.push({ ...g, availability: 'available', localSize: e.size, pinned: e.pinned, lastUsedAt: e.lastUsedAt });
    }
  }
  return out.sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

export function useLocalGames() {
  return useQuery({ queryKey: ['local-games'], queryFn: localGames, networkMode: 'always', staleTime: 5_000 });
}

// ---------------------------------------------------------------------------
// Download for offline play
// ---------------------------------------------------------------------------

/** Ask the service worker to keep these URLs offline. Resolves with per-URL results. */
export function swPrecache(urls: string[]): Promise<{ url: string; ok: boolean }[]> {
  return new Promise((resolve) => {
    const sw = navigator.serviceWorker?.controller;
    if (!sw) return resolve(urls.map((url) => ({ url, ok: false })));
    const ch = new MessageChannel();
    const t = setTimeout(() => resolve(urls.map((url) => ({ url, ok: false }))), 120_000);
    ch.port1.onmessage = (e) => {
      clearTimeout(t);
      resolve((e.data as { results: { url: string; ok: boolean }[] }).results);
    };
    sw.postMessage({ type: 'precache', urls }, [ch.port2]);
  });
}

/** Emulator core files for a system (both builds EmulatorJS may pick). */
export function coreFiles(systemId: string) {
  const engine = webEngine(systemId);
  if (!engine) return [];
  const c = engine.core;
  const variants = c === 'dosbox_pure' ? ['thread-wasm', 'thread-legacy-wasm'] : ['wasm', 'legacy-wasm'];
  return [...variants.map((v) => `/emulatorjs/data/cores/${c}-${v}.data`), `/emulatorjs/data/cores/reports/${c}.json`];
}

/**
 * Make a game playable offline on this device: download and verify the game
 * file, keep its sheet, and cache its emulator core. Pins it so automatic
 * cleanup never removes it.
 */
export async function downloadForOffline(
  g: GameDetail,
  opts: { signal?: AbortSignal; onProgress?: (p: { loaded: number; total: number; speedBps: number }) => void } = {},
) {
  if (!g.file) throw new Error('Este juego no tiene archivo');
  rememberGame(g);
  const cache = await getCache();
  void cache.requestPersistence();
  await cache.download({
    sha256: g.file.sha256,
    size: g.file.size,
    filename: g.file.filename,
    gameId: g.id,
    signal: opts.signal,
    onProgress: opts.onProgress,
    getUrl: async () => (await api<DownloadTicket>(`/api/games/${g.id}/download`, { method: 'POST' })).url,
  });
  await cache.setPinned(g.file.sha256, true);
  const results = await swPrecache(coreFiles(g.systemId));
  // A core variant the server doesn't have (404) is fine; at least one build must be cached.
  const coreOk = results.some((r) => r.ok && r.url.endsWith('.data'));
  return { coreOk };
}
